import React, { useEffect, useState, useCallback } from "react";
import API from "../services/api";
import { toast } from "react-toastify";
import {
  Wallet, RefreshCw, Download, FileText, CheckCircle2,
  Clock, PlusCircle, X, Mail, AlertCircle,
} from "lucide-react";

const STATUS_STYLES = {
  PENDING:   "bg-yellow-100 text-yellow-700 border-yellow-200",
  PROCESSED: "bg-blue-100 text-blue-700 border-blue-200",
  PAID:      "bg-green-100 text-green-700 border-green-200",
  CANCELLED: "bg-red-100 text-red-700 border-red-200",
};

const formatKES = (amt) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency", currency: "KES", maximumFractionDigits: 0,
  }).format(amt || 0);

/* ─── safely extract ward name regardless of whether it's populated or not ─── */
const wardName = (ward) => {
  if (!ward) return "N/A";
  if (typeof ward === "string") return ward;
  if (typeof ward === "object") return ward.name || "N/A";
  return "N/A";
};

/* ============================ CREATE DISBURSEMENT MODAL ============================ */
const CreateDisbursementModal = ({ isOpen, onClose, onCreated, cycles }) => {
  const [approvedApps, setApprovedApps] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [cycleId, setCycleId]             = useState("");
  const [amount, setAmount]               = useState("");
  const [paymentMethod, setPaymentMethod] = useState("BANK_TRANSFER");
  const [saving, setSaving]               = useState(false);
  const [loadingApps, setLoadingApps]     = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingApps(true);
    API.get("/admin/applications", { params: { status: "APPROVED", limit: 200 } })
      .then((res) => setApprovedApps(res.data.data || []))
      .catch(() => toast.error("Failed to load approved applications"))
      .finally(() => setLoadingApps(false));
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedApp = approvedApps.find((a) => a._id === selectedAppId);

  const handleSubmit = async () => {
    if (!selectedAppId || !cycleId || !amount) {
      toast.error("Please complete all required fields");
      return;
    }
    setSaving(true);
    try {
      await API.post("/disbursements", {
        applicationId: selectedAppId,
        cycleId,
        amountAwarded:  Number(amount),
        paymentMethod,
      });
      toast.success("Bursary award created successfully");
      onCreated();
      onClose();
      setSelectedAppId(""); setAmount(""); setCycleId("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create disbursement");
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors bg-white";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">Award Bursary</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Cycle */}
          <div>
            <label className={labelClass}>Bursary Cycle <span className="text-red-500">*</span></label>
            {cycles.length === 0 ? (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-700">
                <AlertCircle size={14} />
                No cycles found. Create a bursary cycle first under Budget & Cycles.
              </div>
            ) : (
              <select value={cycleId} onChange={(e) => setCycleId(e.target.value)} className={inputClass}>
                <option value="">Select cycle</option>
                {cycles.map((c) => (
                  <option key={c._id} value={c._id}>{c.name} — {c.academicYear}</option>
                ))}
              </select>
            )}
          </div>

          {/* Student */}
          <div>
            <label className={labelClass}>
              Approved Student <span className="text-red-500">*</span>
              {loadingApps && <span className="text-xs text-gray-400 ml-2 font-normal">Loading...</span>}
            </label>
            {!loadingApps && approvedApps.length === 0 ? (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-700">
                <AlertCircle size={14} />
                No approved applications found. Approve applications first.
              </div>
            ) : (
              <select value={selectedAppId} onChange={(e) => setSelectedAppId(e.target.value)} className={inputClass}>
                <option value="">Select student</option>
                {approvedApps.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.studentId?.fullName} — {a.institution?.institutionName || "N/A"}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Fee info hint */}
          {selectedApp && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-800 space-y-1">
              <p><strong>Total Fees:</strong> {formatKES(selectedApp.fees?.totalFees)}</p>
              <p><strong>Fee Balance:</strong> {formatKES(selectedApp.fees?.feeBalance)}</p>
              <p className="text-blue-500">Enter an amount not exceeding the fee balance.</p>
            </div>
          )}

          {/* Amount */}
          <div>
            <label className={labelClass}>Amount to Award (KES) <span className="text-red-500">*</span></label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 15000"
              className={inputClass}
            />
          </div>

          {/* Payment method */}
          <div>
            <label className={labelClass}>Payment Method</label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={inputClass}>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="MPESA">M-Pesa</option>
              <option value="CHEQUE">Cheque</option>
              <option value="INSTITUTION_DIRECT">Direct to Institution</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 hover:bg-gray-50 text-sm font-medium transition">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || cycles.length === 0}
            className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white hover:bg-blue-800 text-sm font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            Create Award
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================ MARK AS PAID MODAL ============================ */
const MarkPaidModal = ({ disbursement, onClose, onSaved }) => {
  const [ref, setRef]     = useState("");
  const [saving, setSaving] = useState(false);

  if (!disbursement) return null;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await API.post(`/disbursements/${disbursement._id}/pay`, { paymentReference: ref });
      toast.success("Marked as paid — student notified by email");
      onSaved();
      onClose();
      setRef("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to mark as paid");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Confirm Payment</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400">
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Confirm that <strong>{formatKES(disbursement.amountAwarded)}</strong> has been paid to{" "}
          <strong>{disbursement.student?.fullName}</strong>. An email notification will be sent automatically.
        </p>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Payment Reference <span className="text-xs text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder="e.g. M-Pesa code or bank ref"
          className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary mb-5"
        />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 hover:bg-gray-50 text-sm font-medium transition">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-xl bg-green-600 text-white hover:bg-green-700 text-sm font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <Mail size={14} />
            }
            Confirm & Notify
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================ MAIN PAGE ============================ */
const DisbursementPage = () => {
  const [disbursements, setDisbursements] = useState([]);
  const [cycles, setCycles]               = useState([]);
  const [stats, setStats]                 = useState(null);
  const [loading, setLoading]             = useState(true);
  const [selectedCycle, setSelectedCycle] = useState("");
  const [statusFilter, setStatusFilter]   = useState("");
  const [createOpen, setCreateOpen]       = useState(false);
  const [payTarget, setPayTarget]         = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch each independently so one failure doesn't block the others
      const [disbRes, cyclesRes, statsRes] = await Promise.allSettled([
        API.get("/disbursements", {
          params: {
            ...(selectedCycle  && { cycleId: selectedCycle }),
            ...(statusFilter   && { status:  statusFilter }),
          },
        }),
        API.get("/cycles"),
        API.get("/disbursements/stats", {
          params: { ...(selectedCycle && { cycleId: selectedCycle }) },
        }),
      ]);

      if (disbRes.status   === "fulfilled") setDisbursements(disbRes.value.data);
      else toast.error("Failed to load disbursements");

      if (cyclesRes.status === "fulfilled") setCycles(cyclesRes.value.data);
      if (statsRes.status  === "fulfilled") setStats(statsRes.value.data);
    } finally {
      setLoading(false);
    }
  }, [selectedCycle, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = () => {
    const base = API.defaults.baseURL;
    const qs   = selectedCycle ? `?cycleId=${selectedCycle}` : "";
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    fetch(`${base}/disbursements/export${qs}`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const a  = document.createElement("a");
        a.href   = window.URL.createObjectURL(blob);
        a.download = "disbursements.xlsx";
        a.click();
      })
      .catch(() => toast.error("Export failed"));
  };

  const handleDownloadLetter = (id, name) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    fetch(`${API.defaults.baseURL}/disbursements/${id}/letter`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const a  = document.createElement("a");
        a.href   = window.URL.createObjectURL(blob);
        a.download = `BursaryLetter_${(name || "student").replace(/\s+/g, "_")}.pdf`;
        a.click();
      })
      .catch(() => toast.error("Failed to download letter"));
  };

  const statCards = stats ? [
    { title: "Total Awarded", value: formatKES(stats.totalAmount),  icon: Wallet,       color: "bg-blue-50 text-primary" },
    { title: "Pending",       value: stats.pending,                  icon: Clock,        color: "bg-yellow-50 text-yellow-600" },
    { title: "Paid",          value: stats.paid,                     icon: CheckCircle2, color: "bg-green-50 text-green-600" },
    { title: "Paid Amount",   value: formatKES(stats.paidAmount),    icon: FileText,     color: "bg-purple-50 text-purple-600" },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-primary">Disbursements</h2>
          <p className="text-gray-500 text-sm mt-1">Process and track bursary fund payments to students</p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="bg-primary text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-blue-800 active:scale-95 transition"
        >
          <PlusCircle size={14} /> Award Bursary
        </button>
      </div>

      {/* Stat cards */}
      {stats && (
        <div className="grid md:grid-cols-4 gap-4">
          {statCards.map((s, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-5 flex items-center justify-between border border-gray-100">
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">{s.title}</p>
                <h3 className="text-2xl font-bold text-gray-800">{s.value}</h3>
              </div>
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${s.color}`}>
                <s.icon size={20} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-2xl p-4 flex flex-wrap gap-3 items-center border border-gray-100">
        <select
          value={selectedCycle}
          onChange={(e) => setSelectedCycle(e.target.value)}
          className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary"
        >
          <option value="">All Cycles</option>
          {cycles.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary"
        >
          <option value="">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="PROCESSED">Processed</option>
          <option value="PAID">Paid</option>
          <option value="CANCELLED">Cancelled</option>
        </select>

        <div className="flex gap-2 ml-auto">
          <button
            onClick={fetchData}
            className="bg-secondary text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-blue-700 active:scale-95 transition"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={handleExport}
            className="bg-gray-700 text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-gray-800 active:scale-95 transition"
          >
            <Download size={14} /> Export Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        {loading ? (
          <div className="flex flex-col items-center py-14 gap-3">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 text-sm">Loading disbursements...</p>
          </div>
        ) : disbursements.length === 0 ? (
          <div className="text-center py-14">
            <Wallet size={36} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No disbursements yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Click "Award Bursary" to create the first disbursement.
            </p>
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100">
                <tr>
                  {["Student", "Ward", "Institution", "Amount", "Method", "Status", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {disbursements.map((d) => (
                  <tr key={d._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3.5 font-medium text-gray-800">
                      {d.student?.fullName || "N/A"}
                    </td>
                    {/* ← wardName() safely converts object to string */}
                    <td className="px-4 py-3.5 text-gray-500 text-xs">
                      {wardName(d.student?.ward)}
                    </td>
                    <td className="px-4 py-3.5 text-gray-600 text-xs max-w-[140px] truncate">
                      {d.application?.institution?.institutionName || "N/A"}
                    </td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">
                      {formatKES(d.amountAwarded)}
                    </td>
                    <td className="px-4 py-3.5 text-gray-500 text-xs">
                      {d.paymentMethod?.replace(/_/g, " ") || "—"}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_STYLES[d.status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                        {d.status}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        {d.status !== "PAID" && (
                          <button
                            onClick={() => setPayTarget(d)}
                            className="px-2.5 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition flex items-center gap-1"
                          >
                            <CheckCircle2 size={12} /> Mark Paid
                          </button>
                        )}
                        {d.status === "PAID" && (
                          <button
                            onClick={() => handleDownloadLetter(d._id, d.student?.fullName)}
                            className="px-2.5 py-1.5 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-medium transition flex items-center gap-1"
                          >
                            <FileText size={12} /> Letter
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateDisbursementModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={fetchData}
        cycles={cycles}
      />
      <MarkPaidModal
        disbursement={payTarget}
        onClose={() => setPayTarget(null)}
        onSaved={fetchData}
      />
    </div>
  );
};

export default DisbursementPage;
